// src/ProgramWizardPage.js
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { firePAEvent } from './utils/analytics';
import './ProgramWizardPage.css';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const SPECIALTIES = [
    'Sports', 'Musculoskeletal', 'Neurological', 'Paediatric',
    'Respiratory', "Women's Health", 'Geriatric', 'Occupational Health'
];

const NUM_WEEKS = 5;

const FACILITY_TYPES = ['Clinic', 'Gait lab', 'Performance centre', 'Other'];

function emptySchedule() {
    const s = {};
    DAYS.forEach(d => { s[d] = []; });
    return s;
}

function emptyWeeks() {
    return Array.from({ length: NUM_WEEKS }, () => emptySchedule());
}

const SCHEDULE_TEMPLATES = [
    {
        id: '3day-strength',
        label: '3 Days/Week Strength',
        description: 'Mon / Wed / Fri',
        days: {
            monday: [
                { name: 'Barbell Squat', meta: '4x8' },
                { name: 'Bench Press', meta: '4x8' },
                { name: 'Bent-Over Row', meta: '3x10' },
            ],
            tuesday: [],
            wednesday: [
                { name: 'Deadlift', meta: '4x6' },
                { name: 'Overhead Press', meta: '4x8' },
                { name: 'Pull-Ups', meta: '3x8' },
            ],
            thursday: [],
            friday: [
                { name: 'Front Squat', meta: '4x8' },
                { name: 'Incline Dumbbell Press', meta: '3x10' },
                { name: 'Cable Row', meta: '3x12' },
            ],
            saturday: [],
            sunday: [],
        }
    },
    {
        id: '5day-rehab',
        label: '5 Days/Week Rehab',
        description: 'Mon – Fri',
        days: {
            monday: [
                { name: 'Ankle Circles', meta: '2x15 each' },
                { name: 'Calf Raises', meta: '3x12' },
                { name: 'Seated Knee Extension', meta: '3x10' },
            ],
            tuesday: [
                { name: 'Hip Flexor Stretch', meta: '2x30s each' },
                { name: 'Glute Bridge', meta: '3x12' },
                { name: 'Side-Lying Hip Abduction', meta: '3x10' },
            ],
            wednesday: [
                { name: 'Shoulder Pendulum', meta: '2x20' },
                { name: 'Wall Slides', meta: '3x10' },
                { name: 'External Rotation', meta: '3x12' },
            ],
            thursday: [
                { name: 'Single-Leg Balance', meta: '3x30s each' },
                { name: 'Step-Ups', meta: '3x10 each' },
                { name: 'Hamstring Curl', meta: '3x12' },
            ],
            friday: [
                { name: 'Cat-Cow Stretch', meta: '2x15' },
                { name: 'Bird-Dog', meta: '3x10 each' },
                { name: 'Plank', meta: '3x20s' },
            ],
            saturday: [],
            sunday: [],
        }
    },
    {
        id: 'daily-mobility',
        label: 'Daily Mobility',
        description: 'All 7 days',
        days: {
            monday: [
                { name: 'Cat-Cow', meta: '2x10' },
                { name: 'World’s Greatest Stretch', meta: '2x5 each' },
            ],
            tuesday: [
                { name: 'Hip 90/90', meta: '2x8 each' },
                { name: 'Thoracic Rotation', meta: '2x10 each' },
            ],
            wednesday: [
                { name: 'Ankle Mobility Drill', meta: '2x12 each' },
                { name: 'Shoulder Pass-Through', meta: '2x12' },
            ],
            thursday: [
                { name: 'Deep Squat Hold', meta: '3x30s' },
                { name: 'Prone Cobra', meta: '3x15' },
            ],
            friday: [
                { name: 'Pigeon Stretch', meta: '2x30s each' },
                { name: 'Lat Stretch', meta: '2x30s each' },
            ],
            saturday: [
                { name: 'Foam Roll Full Body', meta: '10 min' },
                { name: 'Butterfly Stretch', meta: '2x30s' },
            ],
            sunday: [
                { name: 'Sun Salutation Flow', meta: '5 rounds' },
                { name: 'Diaphragmatic Breathing', meta: '5 min' },
            ],
        }
    },
    {
        id: 'upper-lower',
        label: 'Upper/Lower Split',
        description: '4 days',
        days: {
            monday: [
                { name: 'Bench Press', meta: '4x8' },
                { name: 'Seated Row', meta: '4x10' },
                { name: 'Lateral Raise', meta: '3x12' },
            ],
            tuesday: [
                { name: 'Barbell Squat', meta: '4x8' },
                { name: 'Romanian Deadlift', meta: '3x10' },
                { name: 'Leg Press', meta: '3x12' },
            ],
            wednesday: [],
            thursday: [
                { name: 'Overhead Press', meta: '4x8' },
                { name: 'Lat Pulldown', meta: '4x10' },
                { name: 'Face Pull', meta: '3x15' },
            ],
            friday: [
                { name: 'Front Squat', meta: '4x8' },
                { name: 'Hip Thrust', meta: '3x12' },
                { name: 'Walking Lunge', meta: '3x10 each' },
            ],
            saturday: [],
            sunday: [],
        }
    },
    {
        id: 'sports-perf',
        label: 'Sports Performance',
        description: '5 days',
        days: {
            monday: [
                { name: 'Power Clean', meta: '5x3' },
                { name: 'Box Jump', meta: '4x5' },
                { name: 'Sprint Intervals', meta: '6x30s' },
            ],
            tuesday: [
                { name: 'Back Squat', meta: '5x5' },
                { name: 'Bulgarian Split Squat', meta: '3x8 each' },
                { name: 'Nordic Curl', meta: '3x6' },
            ],
            wednesday: [
                { name: 'Agility Ladder', meta: '4 patterns' },
                { name: 'Medicine Ball Slam', meta: '4x8' },
                { name: 'Plyo Push-Up', meta: '3x8' },
            ],
            thursday: [
                { name: 'Hang Snatch', meta: '5x3' },
                { name: 'Sled Push', meta: '4x20m' },
                { name: 'Battle Ropes', meta: '4x30s' },
            ],
            friday: [
                { name: 'Tempo Run', meta: '20 min' },
                { name: 'Mobility Circuit', meta: '15 min' },
                { name: 'Core Stability', meta: '3 rounds' },
            ],
            saturday: [],
            sunday: [],
        }
    },
    {
        id: 'weekend-recovery',
        label: 'Weekend Recovery',
        description: 'Sat / Sun only',
        days: {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [
                { name: 'Foam Rolling', meta: '15 min' },
                { name: 'Light Yoga Flow', meta: '20 min' },
                { name: 'Walking', meta: '30 min' },
            ],
            sunday: [
                { name: 'Stretching Circuit', meta: '20 min' },
                { name: 'Breathing Exercises', meta: '10 min' },
                { name: 'Contrast Shower', meta: '5 min' },
            ],
        }
    },
];

function generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
}

/**
 * Click-on-card service selector. The whole card is the click target.
 * - role="checkbox" + aria-checked for screen readers
 * - tabIndex={0} for keyboard focus
 * - Space/Enter toggle selection
 * - Decorative checkbox visual inside (pointer-events: none in CSS)
 */
function ServiceCard({ id, isSelected, onToggle, title, description, meta }) {
    const handleKeyDown = (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            onToggle();
        }
    };
    return (
        <div
            className={`pw-service-card${isSelected ? ' selected' : ''}`}
            role="checkbox"
            aria-checked={isSelected}
            tabIndex={0}
            onClick={onToggle}
            onKeyDown={handleKeyDown}
            data-service={id}
        >
            <span
                className={`pw-service-checkbox${isSelected ? ' checked' : ''}`}
                aria-hidden="true"
            >
                {isSelected && <span className="pw-service-check">&#x2713;</span>}
            </span>
            <div className="pw-service-body">
                <div className="pw-service-title">{title}</div>
                <p>{description}</p>
                <p className="pw-service-meta">{meta}</p>
            </div>
            {isSelected && <span className="pw-service-badge">&#x2713; Selected</span>}
        </div>
    );
}

/** Small (i) info-icon with CSS-only hover/focus tooltip explaining HCPC. */
function HcpcTooltip() {
    return (
        <span className="pw-tooltip-wrap">
            <button
                type="button"
                className="pw-tooltip-trigger"
                aria-label="What is HCPC?"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
                i
            </button>
            <span className="pw-tooltip-content" role="tooltip">
                UK regulator for physiotherapists. Non-UK practitioners can submit equivalent registration, and we review case by case.
            </span>
        </span>
    );
}

/**
 * Mobile step progress strip — replaces the 5-tab indicator on small screens.
 * Counts ACTIVE steps only (so e.g. S3-only shows "Step X of 3").
 */
function MobileStepProgress({ step, stepActive, labels }) {
    const activeSteps = labels
        .map((label, i) => ({ label, idx: i + 1 }))
        .filter(({ idx }) => stepActive[idx]);

    const totalActive = activeSteps.length;
    const currentPos = activeSteps.findIndex(({ idx }) => idx === step) + 1;
    const currentLabel = activeSteps.find(({ idx }) => idx === step)?.label || '';
    const pct = totalActive > 0 ? Math.max(0, (currentPos / totalActive) * 100) : 0;

    return (
        <div className="pw-mobile-step-progress" aria-label={`Step ${currentPos} of ${totalActive}: ${currentLabel}`}>
            <div className="pw-mobile-step-label">
                <strong>Step {currentPos} of {totalActive}:</strong> {currentLabel}
            </div>
            <div className="pw-mobile-step-bar">
                <div className="pw-mobile-step-bar-fill" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

export default function ProgramWizardPage() {
    // Step 0 (Services) is the new entry point; the wizard still uses a 1..N step number.
    // Steps: 1 = Services, 2 = Revenue, 3 = Programme, 4 = Profile, 5 = Review.
    const [step, setStep] = useState(1);

    // Step 1: Service selection
    const [selectedServices, setSelectedServices] = useState([]); // subset of ['s1','s2','s3']

    // Step 2: Revenue Calculator
    const [monthlyPrice, setMonthlyPrice] = useState(30);
    const [subscribers, setSubscribers] = useState(10);
    const [retention, setRetention] = useState(4);

    // Step 3: Programme
    const [programName, setProgramName] = useState('');
    const [description, setDescription] = useState('');
    const [scheduleMode, setScheduleMode] = useState('daygrid');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [describeText, setDescribeText] = useState('');
    const [weeks, setWeeks] = useState(emptyWeeks);
    const [currentWeek, setCurrentWeek] = useState(0);
    const [addFormDay, setAddFormDay] = useState(null);
    const [addFormName, setAddFormName] = useState('');
    const [addFormMeta, setAddFormMeta] = useState('');
    const addMetaRef = useRef(null);
    const [mobileDay, setMobileDay] = useState(0);

    // Step 4: About You
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [occupation, setOccupation] = useState('');
    const [registrationNo, setRegistrationNo] = useState('');
    const [specialty, setSpecialty] = useState('');
    const [country, setCountry] = useState('');
    const [headline, setHeadline] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [linkedinUrl, setLinkedinUrl] = useState('');
    const [instagramUrl, setInstagramUrl] = useState('');
    const [bio, setBio] = useState(
        "Hi, I'm [your name]. I'm a [occupation] specialising in [your speciality]. " +
        "With [X] years of experience, I help clients with [conditions you treat]. " +
        "My approach focuses on [your treatment philosophy]. " +
        "I look forward to supporting you on your journey."
    );

    // Step 4: S2 sub-section
    const [consultSlotsPerWeek, setConsultSlotsPerWeek] = useState(3);
    const [consultDurationMin, setConsultDurationMin] = useState(30);

    // Step 4: S3 sub-section
    const [facilityName, setFacilityName] = useState('');
    const [facilityRegion, setFacilityRegion] = useState('');
    const [facilityType, setFacilityType] = useState('');
    const [facilityTypeOther, setFacilityTypeOther] = useState('');
    const [facilityCapabilities, setFacilityCapabilities] = useState('');

    // Step 5: Review + submit
    const [agreedTerms, setAgreedTerms] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submissionId, setSubmissionId] = useState('');

    // Post-submit pipeline opt-in
    const [eChannelOptIn, setEChannelOptIn] = useState(null); // null = undecided, true/false = chosen

    // Validation
    const [errors, setErrors] = useState({});

    // GA4: wizard_start fires once on mount
    useEffect(() => {
        firePAEvent('wizard_start');
    }, []);

    // Service flags
    const hasS1 = selectedServices.includes('s1');
    const hasS2 = selectedServices.includes('s2');
    const hasS3 = selectedServices.includes('s3');
    const needsProgramme = hasS1 || hasS2;
    const isS3Only = hasS3 && !hasS1 && !hasS2;

    // All steps always navigable — no auto-skipping or locked tabs (2026-06-25 user feedback:
    // even S3-only or zero-selection users can browse Revenue + Programme freely; validation
    // at Step 3 only requires programName when needsProgramme is true).
    const stepActive = { 1: true, 2: true, 3: true, 4: true, 5: true };

    // Revenue calculations
    const monthlyRevenue = subscribers * monthlyPrice;
    const monthlyPlatformFee = monthlyRevenue * 0.20;
    const monthlyKeep = monthlyRevenue - monthlyPlatformFee;
    const yourAnnualRevenue = monthlyKeep * 12;
    // Kept for submit payload backward-compat
    const annualRevenue = monthlyRevenue * 12;
    const platformFee = annualRevenue * 0.20;
    const youKeep = yourAnnualRevenue;

    const toggleService = (svc) => {
        setSelectedServices(prev =>
            prev.includes(svc) ? prev.filter(s => s !== svc) : [...prev, svc]
        );
        // Clear any service-step error
        setErrors(e => {
            const { services, ...rest } = e;
            return rest;
        });
    };

    const validateStep = (s) => {
        const e = {};
        // Step 1 (Services) intentionally ungated 2026-06-25 — let users browse and fill out
        // even with nothing selected, no barriers to exploration.
        if (s === 3) {
            if (needsProgramme && !programName.trim()) e.programName = 'Required';
        }
        if (s === 4) {
            if (!fullName.trim()) e.fullName = 'Required';
            if (!email.trim()) e.email = 'Required';
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Invalid email';

            if (hasS2) {
                if (!registrationNo.trim()) e.registrationNo = 'Required for Personalised';
                if (!consultSlotsPerWeek || consultSlotsPerWeek < 1) e.consultSlotsPerWeek = 'Required';
                if (!consultDurationMin) e.consultDurationMin = 'Required';
            }

            if (hasS3) {
                if (!facilityName.trim()) e.facilityName = 'Required';
                if (!facilityRegion.trim()) e.facilityRegion = 'Required';
                if (!facilityType) e.facilityType = 'Required';
                if (facilityType === 'Other' && !facilityTypeOther.trim()) e.facilityTypeOther = 'Please describe the facility type';
            }
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    // Find the next/previous active step relative to `from`, in the given direction.
    const findStep = (from, dir) => {
        let n = from + dir;
        while (n >= 1 && n <= 5) {
            if (stepActive[n]) return n;
            n += dir;
        }
        return from;
    };

    const goToStep = (target) => {
        // When advancing past the current step, validate the current step first.
        if (target > step && !validateStep(step)) return;
        // Don't allow jumping to a skipped step.
        if (!stepActive[target]) return;
        setStep(target);
        window.scrollTo({ top: document.querySelector('.pw-builder-section')?.offsetTop - 80 || 0, behavior: 'smooth' });
    };

    const goNext = () => {
        if (!validateStep(step)) return;
        const next = findStep(step, +1);
        if (next === step) return;
        setStep(next);
        window.scrollTo({ top: document.querySelector('.pw-builder-section')?.offsetTop - 80 || 0, behavior: 'smooth' });
    };

    const goBack = () => {
        const prev = findStep(step, -1);
        if (prev === step) return;
        setStep(prev);
        window.scrollTo({ top: document.querySelector('.pw-builder-section')?.offsetTop - 80 || 0, behavior: 'smooth' });
    };

    // Schedule helper for current week
    const schedule = weeks[currentWeek];

    const updateCurrentWeek = (updater) => {
        setWeeks(prev => prev.map((w, i) => i === currentWeek ? updater(w) : w));
    };

    const applyTemplate = (template) => {
        setSelectedTemplate(template.id);
        // Apply template to Week 1 only
        const newWeek = {};
        DAYS.forEach(d => {
            newWeek[d] = template.days[d].map(ex => ({
                id: generateId(),
                name: ex.name,
                meta: ex.meta
            }));
        });
        setWeeks(prev => prev.map((w, i) => i === 0 ? newWeek : w));
        setCurrentWeek(0);
        setScheduleMode('daygrid');
    };

    // Exercise management (operates on current week)
    const addExercise = (dayKey) => {
        const name = addFormName.trim();
        if (!name) return;
        updateCurrentWeek(w => ({
            ...w,
            [dayKey]: [...w[dayKey], { id: generateId(), name, meta: addFormMeta.trim() }]
        }));
        setAddFormName('');
        setAddFormMeta('');
        setAddFormDay(null);
    };

    const removeExercise = (dayKey, exId) => {
        updateCurrentWeek(w => ({
            ...w,
            [dayKey]: w[dayKey].filter(ex => ex.id !== exId)
        }));
    };

    const buildPayload = (eChannelOptInValue) => {
        const payload = {
            physioName: fullName.trim(),
            physioEmail: email.trim(),
            registrationNo: registrationNo.trim(),
            selectedServices: [...selectedServices],
            profile: {
                occupation: occupation.trim(),
                specialty,
                country: country.trim(),
                headline: headline.trim(),
                websiteUrl: websiteUrl.trim(),
                linkedinUrl: linkedinUrl.trim(),
                instagramUrl: instagramUrl.trim(),
                bio: bio.trim(),
            },
            eChannelOptIn: eChannelOptInValue === true,
        };

        if (needsProgramme) {
            payload.price = String(monthlyPrice);
            payload.revenueProjection = {
                monthlyPrice,
                subscribers,
                retention,
                monthlyRevenue,
                annualRevenue,
                platformFee,
                youKeep,
            };
            payload.goals = [{
                name: programName.trim(),
                description: description.trim() || describeText.trim(),
                levels: weeks.map((weekSchedule, i) => ({
                    level: i + 1,
                    name: `Week ${i + 1}`,
                    schedule: weekSchedule
                }))
            }];
        }

        if (hasS2) {
            payload.s2 = {
                consultSlotsPerWeek: Number(consultSlotsPerWeek),
                consultDurationMin: Number(consultDurationMin),
            };
        }

        if (hasS3) {
            const effectiveType = facilityType === 'Other' ? facilityTypeOther.trim() : facilityType;
            payload.s3 = {
                facilityName: facilityName.trim(),
                region: facilityRegion.trim(),
                facilityType: effectiveType,
                capabilities: facilityCapabilities.trim(),
            };
        }

        return payload;
    };

    const handleSubmit = async () => {
        if (!agreedTerms) return;
        setSubmitting(true);
        try {
            // First submit captures eChannelOptIn = false; user can flip it via the
            // post-submit screen, which sends a follow-up update.
            const payload = buildPayload(false);

            const response = await fetch('/api/submit-program', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json().catch(() => ({}));
                setSubmissionId(data.submissionId || '');
                setSubmitted(true);
                firePAEvent('wizard_complete', {
                    services: selectedServices.join(',') || 'none',
                    has_s1: hasS1,
                    has_s2: hasS2,
                    has_s3: hasS3,
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                const data = await response.json().catch(() => ({}));
                alert(data.error || 'Failed to submit. Please try again.');
            }
        } catch {
            alert('Network error. Please check your connection and try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEChannelOptIn = async (optedIn) => {
        setEChannelOptIn(optedIn);
        if (!optedIn) return;
        // Best-effort follow-up to flip the flag. Backend may not be ready;
        // we still update the UI to reflect the user's choice.
        try {
            await fetch('/api/submit-program', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...buildPayload(true),
                    submissionId,
                    eChannelOptInUpdate: true,
                })
            });
        } catch {
            // No-op — UI confirmation is enough for now.
        }
    };

    // Computed (current week)
    const totalAllWeeks = weeks.reduce((sum, w) => sum + DAYS.reduce((s, d) => s + w[d].length, 0), 0);

    const formatCurrency = (value) => {
        return '£' + value.toLocaleString('en-GB');
    };

    const STEP_LABELS = ['Services', 'Revenue', 'Programme', 'Profile', 'Review'];

    // --- Post-submit success state ---
    if (submitted) {
        return (
            <div className="pw-page">
                <div className="pw-page-hero pw-page-hero-success">
                    <div className="pw-success-icon-circle">&#x2713;</div>
                    <h1>Submission Received, {fullName.split(' ')[0]}</h1>
                    <p>Your application is now in our review queue.</p>
                </div>
                <div className="pw-section">
                    <div className="pw-success-card">
                        <div className="pw-success-items">
                            <div className="pw-success-item">
                                <span className="pw-success-bullet">&#x2713;</span>
                                Submission received
                            </div>
                            <div className="pw-success-item">
                                <span className="pw-success-bullet">&#x2713;</span>
                                Our team will review your application
                            </div>
                            {hasS1 && (
                                <div className="pw-success-item">
                                    <span className="pw-success-bullet">&#x2713;</span>
                                    Self-Guided: will go live on approval
                                </div>
                            )}
                            {hasS2 && (
                                <div className="pw-success-item">
                                    <span className="pw-success-bullet">&#x2713;</span>
                                    Personalised: pending registration verification
                                </div>
                            )}
                            {hasS3 && (
                                <div className="pw-success-item">
                                    <span className="pw-success-bullet">&#x2713;</span>
                                    Specialised: pending facility partnership review
                                </div>
                            )}
                        </div>
                        {submissionId && <p className="pw-submission-id">Reference: <code>{submissionId}</code></p>}

                        {/* Pipeline opt-in */}
                        <div className="pw-success-next">
                            <h4>Want PA-sourced pipeline on top?</h4>
                            <p style={{ marginBottom: '0.75rem', color: '#525252', lineHeight: 1.6 }}>
                                Once your services are live, you can opt in to receive employer-sourced patients as pipeline.
                                We bring the patient. You set the rate. Earn royalties on every PA-brought patient you take on.
                            </p>
                            <p style={{ marginBottom: '1rem', color: '#666', lineHeight: 1.6, fontSize: '0.9rem', fontStyle: 'italic' }}>
                                Pipeline routing depends on retention, availability, and qualifications. We'll let you know
                                what you qualify for after your first few subscribers.
                            </p>
                            {eChannelOptIn === null && (
                                <div className="pw-btn-row" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                                    <button className="pw-btn pw-btn-secondary" onClick={() => handleEChannelOptIn(false)}>
                                        Not now
                                    </button>
                                    <button className="pw-btn pw-btn-primary" onClick={() => handleEChannelOptIn(true)}>
                                        Yes, opt me in
                                    </button>
                                </div>
                            )}
                            {eChannelOptIn === true && (
                                <p style={{ color: '#1a7a3e', fontWeight: 600, margin: 0 }}>
                                    &#x2713; You're opted in. We'll be in touch with pipeline eligibility once your services go live.
                                </p>
                            )}
                            {eChannelOptIn === false && (
                                <p style={{ color: '#666', margin: 0 }}>
                                    No problem, you can opt in later from your profile settings.
                                </p>
                            )}
                        </div>

                        <div className="pw-success-next">
                            <h4>What Happens Next</h4>
                            <p style={{ marginBottom: '0.75rem', color: '#525252', lineHeight: 1.6 }}>
                                Every application is reviewed by our team to verify professional credentials and suitability
                                before access is granted. We&#39;ll email <strong>{email}</strong> once that is done.
                            </p>
                            <p style={{ marginBottom: '0.75rem', color: '#525252', lineHeight: 1.6 }}>
                                If you don't see the email, <strong>please check your junk or spam folder</strong>, as our messages sometimes land there.
                            </p>
                            <p style={{ color: '#525252', lineHeight: 1.6, fontSize: '0.9rem' }}>
                                Questions in the meantime? Contact us at{' '}
                                <a href="mailto:hello@physioactivity.com" style={{ color: '#4CB6C3' }}>hello@physioactivity.com</a>{' '}
                                quoting your reference above.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // --- Main wizard ---
    return (
        <div className="pw-page">
            {/* Back link to solutions page + sample-page link */}
            <div className="pw-back-link-bar">
                <Link to="/physiotherapists" className="pw-back-link">
                    &larr; Learn how PhysioActivity works
                </Link>
                <a
                    href="/profile/sample"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pw-back-link pw-sample-page-link"
                >
                    See a sample page &rarr;
                </a>
            </div>

            {/* Headline above tabs */}
            <div className="pw-page-hero">
                <h1>Extend Your Care. Grow Your Practice.</h1>
            </div>

            {/* Step Tabs */}
            <div className="pw-section">
                <div className="pw-builder-section">
                    <MobileStepProgress step={step} stepActive={stepActive} labels={STEP_LABELS} />
                    <div className="pw-step-indicator">
                        {STEP_LABELS.map((label, i) => {
                            const idx = i + 1;
                            const isActive = step === idx;
                            const isSkipped = !stepActive[idx];
                            const cls = [
                                'pw-step-tab',
                                isActive ? 'active' : '',
                                isSkipped ? 'skipped' : '',
                            ].filter(Boolean).join(' ');
                            return (
                                <button
                                    key={i}
                                    className={cls}
                                    onClick={() => { if (!isSkipped) goToStep(idx); }}
                                    type="button"
                                    disabled={isSkipped}
                                    title={isSkipped ? 'Not applicable for the services you selected' : ''}
                                >
                                    <span className="pw-step-num">{idx}</span> {label}
                                </button>
                            );
                        })}
                    </div>

                    {/* ======================== Step 1: Services ======================== */}
                    {step === 1 && (
                        <div className="pw-step-content">
                            <div className="pw-adherence-section pw-adherence-top">
                                <h3>Which services do you want to offer?</h3>
                                <p className="pw-step-intro" style={{ color: '#525252', marginBottom: '0.5rem' }}>
                                    Pick one or more. You can change this later from your profile.
                                </p>
                                <p className="pw-time-estimate">
                                    Takes about 5 minutes. You can browse and decide later. Nothing is committed until Step 5.
                                </p>

                                <div className="pw-service-grid">
                                    <ServiceCard
                                        id="s1"
                                        isSelected={hasS1}
                                        onToggle={() => toggleService('s1')}
                                        title="Self-Guided"
                                        description="Marketplace-listed programme delivered through the app."
                                        meta="Registration verified before listing."
                                    />

                                    <ServiceCard
                                        id="s2"
                                        isSelected={hasS2}
                                        onToggle={() => toggleService('s2')}
                                        title="Personalised"
                                        description={
                                            <>
                                                Vetted (HCPC-registered
                                                <HcpcTooltip />
                                                ) physio offering programme + monthly remote consult.
                                            </>
                                        }
                                        meta="Approval-gated listing."
                                    />

                                    <ServiceCard
                                        id="s3"
                                        isSelected={hasS3}
                                        onToggle={() => toggleService('s3')}
                                        title="Specialised (facilities-based)"
                                        description="In-person assessment via our partner facilities network."
                                        meta="Requires facility affiliation."
                                    />
                                </div>

                                {/* S3-only skip hint removed 2026-06-25 — all steps are now navigable
                                    regardless of service selection; nothing is auto-skipped. */}

                                <div className="pw-service-hints">
                                    <p>Most freelancers pick <strong>Self-Guided + Personalised</strong>.</p>
                                    <p>Clinics with movement labs usually pick <strong>Specialised</strong>.</p>
                                    <p>You can mix any combination, or browse without picking, and you can decide later.</p>
                                </div>
                            </div>

                            <div className="pw-btn-row">
                                <div />
                                <button
                                    className="pw-btn pw-btn-primary"
                                    onClick={goNext}
                                >
                                    Next &rarr;
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ======================== Step 2: Revenue Calculator ======================== */}
                    {step === 2 && stepActive[2] && (
                        <div className="pw-step-content">
                            <div className="pw-adherence-section pw-adherence-top">
                                <h3>5-Level Adherence-Based Progression</h3>
                                <div className="pw-adherence-grid">
                                    <div className="pw-adherence-card pw-adherence-down">
                                        <strong>&lt; 30% adherence</strong>
                                        <span>Down one level</span>
                                    </div>
                                    <div className="pw-adherence-card pw-adherence-stay">
                                        <strong>30 &ndash; 70% adherence</strong>
                                        <span>Stays at current level</span>
                                    </div>
                                    <div className="pw-adherence-card pw-adherence-up">
                                        <strong>&gt; 70% adherence</strong>
                                        <span>Up one level</span>
                                    </div>
                                </div>
                                <p className="pw-adherence-note">
                                    Each level = 1 month. 5 levels = 5+ months recurring revenue per client.
                                </p>
                            </div>

                            <div className="pw-calc-layout">
                                <div className="pw-calc-sliders">
                                    <div className="pw-slider-group">
                                        <label>Monthly price per subscriber</label>
                                        <div className="pw-slider-row">
                                            <input
                                                type="range"
                                                min={20}
                                                max={100}
                                                step={1}
                                                value={monthlyPrice}
                                                onChange={e => setMonthlyPrice(Number(e.target.value))}
                                                className="pw-range"
                                            />
                                            <span className="pw-slider-value">{formatCurrency(monthlyPrice)}</span>
                                        </div>
                                    </div>
                                    <div className="pw-slider-group">
                                        <label>Expected subscribers</label>
                                        <div className="pw-slider-row">
                                            <input
                                                type="range"
                                                min={1}
                                                max={200}
                                                step={1}
                                                value={subscribers}
                                                onChange={e => setSubscribers(Number(e.target.value))}
                                                className="pw-range"
                                            />
                                            <span className="pw-slider-value">{subscribers}</span>
                                        </div>
                                    </div>
                                    <div className="pw-slider-group">
                                        <label>Average retention (months)</label>
                                        <div className="pw-slider-row">
                                            <input
                                                type="range"
                                                min={1}
                                                max={12}
                                                step={1}
                                                value={retention}
                                                onChange={e => setRetention(Number(e.target.value))}
                                                className="pw-range"
                                            />
                                            <span className="pw-slider-value">{retention} mo</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="pw-calc-results">
                                    <div className="pw-calc-line">
                                        <span>Monthly revenue</span>
                                        <strong>{formatCurrency(monthlyRevenue)}</strong>
                                    </div>
                                    <div className="pw-calc-line pw-calc-fee">
                                        <span>Platform fee (20%)</span>
                                        <strong>&minus;{formatCurrency(monthlyPlatformFee)}</strong>
                                    </div>
                                    <div className="pw-calc-line pw-calc-total">
                                        <span>Projected annual revenue</span>
                                        <strong>{formatCurrency(yourAnnualRevenue)}</strong>
                                    </div>
                                </div>
                            </div>

                            <div className="pw-btn-row">
                                <button className="pw-btn pw-btn-secondary" onClick={goBack}>&larr; Back</button>
                                <button className="pw-btn pw-btn-primary" onClick={goNext}>Build My Programme &rarr;</button>
                            </div>
                        </div>
                    )}

                    {/* ======================== Step 3: Programme Builder ======================== */}
                    {step === 3 && stepActive[3] && (
                        <div className="pw-step-content">
                            <div className="pw-form-grid">
                                <div className={`pw-form-group${errors.programName ? ' error' : ''}`}>
                                    <label>Programme Name *</label>
                                    <input type="text" placeholder="e.g. General Strength" value={programName} onChange={e => setProgramName(e.target.value)} />
                                    {errors.programName && <span className="pw-form-error">{errors.programName}</span>}
                                </div>
                                <div className="pw-form-group">
                                    <label>Monthly Price (&pound;)</label>
                                    <input
                                        type="number"
                                        min={20}
                                        step={1}
                                        value={monthlyPrice}
                                        onChange={e => setMonthlyPrice(Math.max(20, Number(e.target.value) || 20))}
                                    />
                                    <span className="pw-form-hint">Pre-filled from the calculator step.</span>
                                </div>
                                <div className="pw-form-group full-width">
                                    <label>Description (optional)</label>
                                    <textarea placeholder="Briefly describe what this programme is for..." value={description} onChange={e => setDescription(e.target.value)} />
                                </div>
                            </div>

                            {/* Schedule Input */}
                            <div className="pw-schedule-section">
                                <h3>Weekly Schedule</h3>
                                <p className="pw-step-intro">Build a weekly schedule for each of the 5 progression weeks. Clients will progress through them based on their adherence.</p>

                                {/* Week Selector */}
                                <div className="pw-week-selector">
                                    {Array.from({ length: NUM_WEEKS }, (_, i) => {
                                        const weekTotal = DAYS.reduce((sum, d) => sum + weeks[i][d].length, 0);
                                        return (
                                            <button
                                                key={i}
                                                type="button"
                                                className={`pw-week-tab${currentWeek === i ? ' active' : ''}${weekTotal > 0 ? ' has-content' : ''}`}
                                                onClick={() => { setCurrentWeek(i); setAddFormDay(null); }}
                                            >
                                                <span className="pw-week-label">Week {i + 1}</span>
                                                <span className="pw-week-count">{weekTotal} ex</span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Template Mode */}
                                {scheduleMode === 'template' && (
                                    <div className="pw-template-grid">
                                        {SCHEDULE_TEMPLATES.map(tmpl => (
                                            <button
                                                key={tmpl.id}
                                                type="button"
                                                className={`pw-template-card${selectedTemplate === tmpl.id ? ' selected' : ''}`}
                                                onClick={() => applyTemplate(tmpl)}
                                            >
                                                <strong>{tmpl.label}</strong>
                                                <span>{tmpl.description}</span>
                                            </button>
                                        ))}
                                        {selectedTemplate && (
                                            <p className="pw-template-hint">
                                                Template applied to Week 1. Switch to "Day Grid" to customise, then build Weeks 2&ndash;5.
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Describe Mode */}
                                {scheduleMode === 'describe' && (
                                    <div className="pw-describe-mode">
                                        <textarea
                                            placeholder="Describe your programme in your own words. We'll help you structure it."
                                            value={describeText}
                                            onChange={e => setDescribeText(e.target.value)}
                                            rows={6}
                                        />
                                    </div>
                                )}

                                {/* Day Grid Mode */}
                                {scheduleMode === 'daygrid' && (
                                    <>
                                        {/* Mobile day selector */}
                                        <div className="pw-mobile-day-selector">
                                            {DAY_LABELS.map((label, i) => (
                                                <button
                                                    key={i}
                                                    className={`pw-day-tab${mobileDay === i ? ' active' : ''}${schedule[DAYS[i]].length > 0 ? ' has-exercises' : ''}`}
                                                    onClick={() => setMobileDay(i)}
                                                    type="button"
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Desktop: full week grid */}
                                        <div className="pw-week-grid">
                                            {DAYS.map((dayKey, i) => (
                                                <div className={`pw-day-card${schedule[dayKey].length === 0 && addFormDay !== dayKey ? ' rest-day' : ''}`} key={dayKey}>
                                                    <div className="pw-day-header">
                                                        {DAY_LABELS[i]}
                                                        <span className="pw-day-count">{schedule[dayKey].length}</span>
                                                    </div>
                                                    <div className="pw-day-exercises">
                                                        {schedule[dayKey].length === 0 && addFormDay !== dayKey && (
                                                            <span className="pw-rest-label">Rest Day</span>
                                                        )}
                                                        {schedule[dayKey].map(ex => (
                                                            <div className="pw-exercise-item" key={ex.id}>
                                                                <span className="pw-ex-name">{ex.name}</span>
                                                                {ex.meta && <span className="pw-ex-detail">{ex.meta}</span>}
                                                                <button className="pw-ex-remove" onClick={() => removeExercise(dayKey, ex.id)} title="Remove">&times;</button>
                                                            </div>
                                                        ))}
                                                        {addFormDay === dayKey ? (
                                                            <div className="pw-add-form">
                                                                <input
                                                                    type="text"
                                                                    placeholder="Exercise name"
                                                                    value={addFormName}
                                                                    onChange={e => setAddFormName(e.target.value)}
                                                                    onKeyDown={e => { if (e.key === 'Enter' && addFormName.trim()) addMetaRef.current?.focus(); }}
                                                                    autoFocus
                                                                />
                                                                <input
                                                                    ref={addMetaRef}
                                                                    type="text"
                                                                    placeholder="Sets x Reps (e.g. 4x10)"
                                                                    value={addFormMeta}
                                                                    onChange={e => setAddFormMeta(e.target.value)}
                                                                    onKeyDown={e => { if (e.key === 'Enter') addExercise(dayKey); }}
                                                                />
                                                                <div className="pw-add-form-actions">
                                                                    <button className="pw-btn-sm pw-btn-primary" onClick={() => addExercise(dayKey)}>Add</button>
                                                                    <button className="pw-btn-sm pw-btn-secondary" onClick={() => { setAddFormDay(null); setAddFormName(''); setAddFormMeta(''); }}>Cancel</button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <button className="pw-add-exercise-btn" onClick={() => setAddFormDay(dayKey)}>+ Add Exercise</button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Mobile: single day view */}
                                        <div className="pw-mobile-day-view">
                                            <div className="pw-day-card">
                                                <div className="pw-day-header">
                                                    {DAY_LABELS[mobileDay]}
                                                    <span className="pw-day-count">{schedule[DAYS[mobileDay]].length}</span>
                                                </div>
                                                <div className="pw-day-exercises">
                                                    {schedule[DAYS[mobileDay]].map(ex => (
                                                        <div className="pw-exercise-item" key={ex.id}>
                                                            <span className="pw-ex-name">{ex.name}</span>
                                                            {ex.meta && <span className="pw-ex-detail">{ex.meta}</span>}
                                                            <button className="pw-ex-remove" onClick={() => removeExercise(DAYS[mobileDay], ex.id)} title="Remove">&times;</button>
                                                        </div>
                                                    ))}
                                                    {addFormDay === DAYS[mobileDay] ? (
                                                        <div className="pw-add-form">
                                                            <input type="text" placeholder="Exercise name" value={addFormName} onChange={e => setAddFormName(e.target.value)} autoFocus onKeyDown={e => { if (e.key === 'Enter' && addFormName.trim()) addMetaRef.current?.focus(); }} />
                                                            <input ref={addMetaRef} type="text" placeholder="Sets x Reps" value={addFormMeta} onChange={e => setAddFormMeta(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addExercise(DAYS[mobileDay]); }} />
                                                            <div className="pw-add-form-actions">
                                                                <button className="pw-btn-sm pw-btn-primary" onClick={() => addExercise(DAYS[mobileDay])}>Add</button>
                                                                <button className="pw-btn-sm pw-btn-secondary" onClick={() => { setAddFormDay(null); setAddFormName(''); setAddFormMeta(''); }}>Cancel</button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <button className="pw-add-exercise-btn" onClick={() => setAddFormDay(DAYS[mobileDay])}>+ Add Exercise</button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="pw-btn-row">
                                <button className="pw-btn pw-btn-secondary" onClick={goBack}>&larr; Back</button>
                                <button className="pw-btn pw-btn-primary" onClick={goNext}>Next: Profile &rarr;</button>
                            </div>
                        </div>
                    )}

                    {/* ======================== Step 4: Profile (+ S2 + S3 sub-sections) ======================== */}
                    {step === 4 && (
                        <div className="pw-step-content">
                            <div className="pw-form-grid">
                                {/* Row 1: Name + Email */}
                                <div className={`pw-form-group${errors.fullName ? ' error' : ''}`}>
                                    <label>Full Name *</label>
                                    <input type="text" placeholder="e.g. Sarah Johnson" value={fullName} onChange={e => setFullName(e.target.value)} />
                                    {errors.fullName && <span className="pw-form-error">{errors.fullName}</span>}
                                </div>
                                <div className={`pw-form-group${errors.email ? ' error' : ''}`}>
                                    <label>Email *</label>
                                    <input type="email" placeholder="e.g. sarah@clinic.com" value={email} onChange={e => setEmail(e.target.value)} />
                                    {errors.email && <span className="pw-form-error">{errors.email}</span>}
                                </div>

                                {/* Row 2: Occupation + Registration Number */}
                                <div className="pw-form-group">
                                    <label>Occupation</label>
                                    <input type="text" placeholder="e.g. Physiotherapist, Sports Therapist, Personal Trainer" value={occupation} onChange={e => setOccupation(e.target.value)} />
                                </div>
                                <div className={`pw-form-group${errors.registrationNo ? ' error' : ''}`}>
                                    <label>Registration No.{hasS2 ? ' *' : ''}</label>
                                    <input
                                        type="text"
                                        placeholder={hasS2 ? 'HCPC registration (required for Personalised)' : 'e.g. HCPC, CIMSPA, REPs (optional)'}
                                        value={registrationNo}
                                        onChange={e => setRegistrationNo(e.target.value)}
                                    />
                                    {errors.registrationNo && <span className="pw-form-error">{errors.registrationNo}</span>}
                                </div>

                                {/* Row 3: Speciality + Country */}
                                <div className="pw-form-group">
                                    <label>Speciality</label>
                                    <input type="text" placeholder="e.g. Sports Rehabilitation, Chronic Pain, Mobility" value={specialty} onChange={e => setSpecialty(e.target.value)} />
                                </div>
                                <div className="pw-form-group">
                                    <label>Country</label>
                                    <input type="text" placeholder="e.g. United Kingdom" value={country} onChange={e => setCountry(e.target.value)} />
                                </div>

                                {/* Row 4: Headline (full width) */}
                                <div className="pw-form-group full-width">
                                    <label>Headline</label>
                                    <input type="text" placeholder="e.g. Sports Specialist focused on running injuries" value={headline} onChange={e => setHeadline(e.target.value)} />
                                </div>

                                {/* Row 5: Website + LinkedIn + Instagram */}
                                <div className="pw-form-group full-width">
                                    <label>Website &amp; Social</label>
                                    <div className="pw-social-row">
                                        <input type="url" placeholder="https://yourclinic.com" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} />
                                        <input type="url" placeholder="LinkedIn URL" value={linkedinUrl} onChange={e => setLinkedinUrl(e.target.value)} />
                                        <input type="url" placeholder="Instagram URL" value={instagramUrl} onChange={e => setInstagramUrl(e.target.value)} />
                                    </div>
                                </div>

                                {/* Row 6: Short Bio */}
                                <div className="pw-form-group full-width">
                                    <label>Short Bio</label>
                                    <textarea value={bio} onChange={e => setBio(e.target.value)} rows={5} />
                                    <span className="pw-form-hint">Pre-filled template. Edit the [bracketed] sections to make it yours.</span>
                                </div>
                            </div>

                            <p className="pw-profile-note">Only name and email are required for the core profile. Service-specific fields below are required when their service is selected.</p>

                            {/* S2 sub-section */}
                            {hasS2 && (
                                <div className="pw-sub-section">
                                    <h3>Personalised setup</h3>
                                    <p className="pw-sub-intro">
                                        Personalised listings are reviewed to confirm HCPC standing and consult setup.
                                        You can keep building during review.
                                    </p>
                                    <div className="pw-form-grid">
                                        <div className={`pw-form-group${errors.consultSlotsPerWeek ? ' error' : ''}`}>
                                            <label>Consult slots per week *</label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={10}
                                                step={1}
                                                value={consultSlotsPerWeek}
                                                onChange={e => setConsultSlotsPerWeek(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                                            />
                                            <span className="pw-form-hint">How many monthly-consult slots you can host each week (1&ndash;10).</span>
                                            {errors.consultSlotsPerWeek && <span className="pw-form-error">{errors.consultSlotsPerWeek}</span>}
                                        </div>
                                        <div className={`pw-form-group${errors.consultDurationMin ? ' error' : ''}`}>
                                            <label>Consult duration *</label>
                                            <select value={consultDurationMin} onChange={e => setConsultDurationMin(Number(e.target.value))}>
                                                <option value={15}>15 minutes</option>
                                                <option value={30}>30 minutes</option>
                                                <option value={45}>45 minutes</option>
                                                <option value={60}>60 minutes</option>
                                            </select>
                                            {errors.consultDurationMin && <span className="pw-form-error">{errors.consultDurationMin}</span>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* S3 sub-section */}
                            {hasS3 && (
                                <div className="pw-sub-section">
                                    <h3>Specialised: facility details</h3>
                                    <p className="pw-sub-intro">
                                        Specialised is partnership-led. We&#39;ll review your facility and reach out.
                                        While we review, you can still set up Self-Guided or Personalised listings above.
                                    </p>
                                    <div className="pw-form-grid">
                                        <div className={`pw-form-group${errors.facilityName ? ' error' : ''}`}>
                                            <label>Facility / clinic name *</label>
                                            <input
                                                type="text"
                                                placeholder="Name of your clinic or facility"
                                                value={facilityName}
                                                onChange={e => setFacilityName(e.target.value)}
                                            />
                                            {errors.facilityName && <span className="pw-form-error">{errors.facilityName}</span>}
                                        </div>
                                        <div className={`pw-form-group${errors.facilityRegion ? ' error' : ''}`}>
                                            <label>Region / city *</label>
                                            <input
                                                type="text"
                                                placeholder="e.g. Greater London, Manchester"
                                                value={facilityRegion}
                                                onChange={e => setFacilityRegion(e.target.value)}
                                            />
                                            {errors.facilityRegion && <span className="pw-form-error">{errors.facilityRegion}</span>}
                                        </div>
                                        <div className={`pw-form-group full-width${errors.facilityType ? ' error' : ''}`}>
                                            <label>Facility type *</label>
                                            <div className="pw-radio-row">
                                                {FACILITY_TYPES.map(type => (
                                                    <label key={type} className={`pw-radio-pill${facilityType === type ? ' selected' : ''}`}>
                                                        <input
                                                            type="radio"
                                                            name="facilityType"
                                                            value={type}
                                                            checked={facilityType === type}
                                                            onChange={() => setFacilityType(type)}
                                                        />
                                                        {type}
                                                    </label>
                                                ))}
                                            </div>
                                            {facilityType === 'Other' && (
                                                <input
                                                    type="text"
                                                    placeholder="Describe the facility type"
                                                    value={facilityTypeOther}
                                                    onChange={e => setFacilityTypeOther(e.target.value)}
                                                    style={{ marginTop: '0.5rem' }}
                                                />
                                            )}
                                            {errors.facilityType && <span className="pw-form-error">{errors.facilityType}</span>}
                                            {errors.facilityTypeOther && <span className="pw-form-error">{errors.facilityTypeOther}</span>}
                                        </div>
                                        <div className="pw-form-group full-width">
                                            <label>In-person assessment capabilities</label>
                                            <textarea
                                                placeholder="Describe specific equipment, procedures, and assessments you offer in-person."
                                                value={facilityCapabilities}
                                                onChange={e => setFacilityCapabilities(e.target.value)}
                                                rows={4}
                                            />
                                            <span className="pw-form-hint">Be specific about what makes your facility distinctive.</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="pw-btn-row">
                                <button className="pw-btn pw-btn-secondary" onClick={goBack}>&larr; Back</button>
                                <button className="pw-btn pw-btn-primary" onClick={goNext}>Next: Review &rarr;</button>
                            </div>
                        </div>
                    )}

                    {/* ======================== Step 5: Review & Submit ======================== */}
                    {step === 5 && (
                        <div className="pw-step-content">
                            {/* Per-service summary */}
                            <div className="pw-review-section">
                                <h3>You're Applying For</h3>
                                <ul className="pw-what-you-get">
                                    {hasS1 && (
                                        <li>
                                            <strong>Self-Guided</strong>: Programme "{programName || '(unnamed)'}" ready to list.
                                            <span style={{ color: '#1a7a3e', marginLeft: '0.5rem' }}>Status: will go live on submit.</span>
                                        </li>
                                    )}
                                    {hasS2 && (
                                        <li>
                                            <strong>Personalised</strong>: Programme "{programName || '(unnamed)'}" + monthly consult
                                            ({consultSlotsPerWeek}/week, {consultDurationMin} min).
                                            <span style={{ color: '#666', marginLeft: '0.5rem' }}>Status: pending approval.</span>
                                        </li>
                                    )}
                                    {hasS3 && (
                                        <li>
                                            <strong>Specialised</strong>: Facility "{facilityName || '(unnamed)'}" in {facilityRegion || '(region)'}.
                                            <span style={{ color: '#666', marginLeft: '0.5rem' }}>Status: pending partnership review.</span>
                                        </li>
                                    )}
                                </ul>
                            </div>

                            {/* Programme Summary (only if S1 or S2) */}
                            {needsProgramme && (
                                <div className="pw-review-section">
                                    <h3>Programme Summary</h3>
                                    <div className="pw-review-meta">
                                        <div className="pw-review-meta-item"><div className="pw-label">Programme</div><div className="pw-value">{programName}</div></div>
                                        <div className="pw-review-meta-item"><div className="pw-label">Total Exercises</div><div className="pw-value">{totalAllWeeks} across 5 weeks</div></div>
                                    </div>
                                    <div className="pw-review-levels-mini" style={{ marginTop: '1rem' }}>
                                        {weeks.map((w, i) => {
                                            const total = DAYS.reduce((sum, d) => sum + w[d].length, 0);
                                            return (
                                                <span className="pw-review-level-pill" key={i}>Week {i + 1}: {total} ex</span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Revenue Recap (only if S1 or S2) */}
                            {needsProgramme && (
                                <div className="pw-review-section">
                                    <h3>Revenue Projection</h3>
                                    <div className="pw-review-meta">
                                        <div className="pw-review-meta-item"><div className="pw-label">Monthly Revenue</div><div className="pw-value">{formatCurrency(monthlyRevenue)}</div></div>
                                        <div className="pw-review-meta-item"><div className="pw-label">Platform Fee (20%)</div><div className="pw-value">&minus;{formatCurrency(monthlyPlatformFee)}</div></div>
                                        <div className="pw-review-meta-item"><div className="pw-label">Projected Annual Revenue</div><div className="pw-value pw-value-highlight">{formatCurrency(yourAnnualRevenue)}</div></div>
                                    </div>
                                </div>
                            )}

                            {/* Review Process */}
                            <div className="pw-review-section">
                                <h3>How Review Works</h3>
                                <p style={{ color: '#525252', lineHeight: 1.6, marginBottom: '0.75rem' }}>
                                    Every submission is reviewed by our team before access is granted. We check professional
                                    credentials, registration validity, and suitability to keep the platform safe for clients.
                                </p>
                                <p style={{ color: '#525252', lineHeight: 1.6, marginBottom: '0.75rem' }}>
                                    You'll hear back by email at <strong>{email || 'the address you provided'}</strong>.
                                    {isS3Only ? (
                                        <> Specialised facilities are reviewed with you.</>
                                    ) : (
                                        <> Self-Guided listings are reviewed before they go live{hasS2 || hasS3 ? '; Personalised and Specialised are reviewed too' : ''}.</>
                                    )}
                                </p>
                                <p style={{ color: '#666', lineHeight: 1.6, fontSize: '0.9rem', fontStyle: 'italic' }}>
                                    No payment is taken at this stage. If your application is approved, we'll explain payment options
                                    in the approval email.
                                </p>
                            </div>

                            {/* What happens after I submit? — collapsible */}
                            <details className="pw-after-submit-expander">
                                <summary>What happens after I submit?</summary>
                                <div className="pw-after-submit-content">
                                    {selectedServices.length === 0 ? (
                                        <p className="pw-after-submit-empty">
                                            You haven't picked any services yet, and that's fine. Submit to register interest
                                            and we'll follow up; you can come back and add services any time.
                                        </p>
                                    ) : (
                                        <>
                                            <ul className="pw-after-submit-list">
                                                {hasS1 && (
                                                    <li>
                                                        <strong>Self-Guided:</strong> listings go live once reviewed.
                                                    </li>
                                                )}
                                                {hasS2 && (
                                                    <li>
                                                        <strong>Personalised:</strong> applications reviewed. We check your HCPC standing and consult setup, then email you when your listing is live.
                                                    </li>
                                                )}
                                                {hasS3 && (
                                                    <li>
                                                        <strong>Specialised (facilities-based):</strong> facility partnership reviewed
                                                        once reviewed. We'll reach out about the facility details and partnership terms.
                                                    </li>
                                                )}
                                            </ul>
                                            <p className="pw-after-submit-note">
                                                We'll email you at every step for each path you applied for.
                                            </p>
                                        </>
                                    )}
                                </div>
                            </details>

                            {/* Terms */}
                            <div className="pw-terms-check">
                                <input type="checkbox" id="pw-terms" checked={agreedTerms} onChange={e => setAgreedTerms(e.target.checked)} />
                                <label htmlFor="pw-terms">
                                    I confirm the details I've provided are accurate{needsProgramme ? ', that this programme is my own work,' : ''} and I understand
                                    my submission will be reviewed before access is granted. I agree to the{' '}
                                    <a href="/TermsOfService">Terms of Service</a>.
                                </label>
                            </div>

                            <div className="pw-btn-row">
                                <button className="pw-btn pw-btn-secondary" onClick={goBack}>&larr; Back</button>
                                <button
                                    className="pw-btn pw-btn-submit"
                                    onClick={handleSubmit}
                                    disabled={!agreedTerms || submitting}
                                >
                                    {submitting ? 'Submitting...' : 'Submit for Review'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
